import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { api, ApiError, type User } from '../../api';
import { useMe } from '../../hooks';

export default function Register() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    real_name: '',
    unit: '',
    phone: '',
    student_no: '',
    password: '',
    confirm: '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (me.data) return <Navigate to="/" replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (form.password.length < 8) {
      setError('密码至少 8 位');
      return;
    }
    if (form.password !== form.confirm) {
      setError('两次输入的密码不一致');
      return;
    }
    setBusy(true);
    try {
      await api<{ user: User }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          real_name: form.real_name.trim(),
          unit: form.unit.trim(),
          phone: form.phone.trim(),
          student_no: form.student_no.trim(),
          password: form.password,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_phone') {
        setError('请填写 11 位中国大陆手机号');
      } else if (err instanceof ApiError && err.code === 'phone_taken') {
        setError('该手机号已注册，请直接登录');
      } else if (err instanceof ApiError && err.code === 'too_many_attempts') {
        setError('注册过于频繁，请稍后再试');
      } else if (err instanceof ApiError && err.code === 'invalid_request') {
        setError('请检查资料：姓名和单位至少 2 个字符，密码至少 8 位');
      } else {
        setError('注册失败，请稍后再试');
      }
    } finally {
      setBusy(false);
    }
  };

  const update = (key: keyof typeof form, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <div className="login-page register-page">
      <section className="login-visual">
        <div>
          <img
            className="login-owner"
            src="/brand/lab-logo-light.png"
            alt="碳捕集与能源利用数智实验室"
          />
          <div className="login-badge">
            <i />
            访客账号 · 自助注册
          </div>
          <h1>先看看我们在做什么，再决定加入。</h1>
          <p>
            注册后可浏览公开的研究方向、发布日志与成员简历。组内师生账号需管理员调整角色后开放内部数据；实验控制还需逐台设备单独授权。
          </p>
        </div>
        <div className="login-foot">
          <span>碳捕集与能源利用数智实验室</span>
          <span>CCS 中试装置</span>
          <span>v0.1</span>
        </div>
      </section>

      <section className="login-form-wrap">
        <form className="login-card login-card-wide" onSubmit={submit}>
          <div className="brand">
            <img className="brand-mark" src="/brand/lab-emblem.png" alt="" />
            <div>
              碳捕集与能源利用数智实验室
              <small>CEUS · Digital Smart Lab</small>
            </div>
          </div>
          <h2>申请账号</h2>
          <p className="sub">新账号默认为访客。请填写真实资料，手机号同时作为登录账号。</p>

          {error ? <div className="error">{error}</div> : null}

          <div className="field">
            <label htmlFor="register-name">真实姓名</label>
            <input
              id="register-name"
              className="input"
              autoComplete="name"
              value={form.real_name}
              onChange={(event) => update('real_name', event.target.value)}
              placeholder="用于后台核对身份"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="register-unit">所属单位</label>
            <input
              id="register-unit"
              className="input"
              value={form.unit}
              onChange={(event) => update('unit', event.target.value)}
              placeholder="学校 / 院系 / 公司"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="register-phone">手机号</label>
            <input
              id="register-phone"
              className="input"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              value={form.phone}
              onChange={(event) => update('phone', event.target.value)}
              placeholder="11 位手机号，登录时使用"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="register-student-no">学号（选填）</label>
            <input
              id="register-student-no"
              className="input"
              value={form.student_no}
              onChange={(event) => update('student_no', event.target.value)}
              placeholder="在校学生可填写"
            />
          </div>

          <div className="field">
            <label htmlFor="register-password">密码</label>
            <input
              id="register-password"
              className="input"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(event) => update('password', event.target.value)}
              placeholder="至少 8 位"
              minLength={8}
              required
            />
          </div>

          <div className="field">
            <label htmlFor="register-confirm">确认密码</label>
            <input
              id="register-confirm"
              className="input"
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              onChange={(event) => update('confirm', event.target.value)}
              placeholder="再输入一次密码"
              minLength={8}
              required
            />
          </div>

          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? '提交中…' : '注册并进入门户'}
          </button>

          <div className="login-alt">
            已有账号？<Link to="/login">去登录</Link>
          </div>

          <div className="login-note">
            访客仅能浏览公开内容。注册信息会进入审计日志，仅管理员可查看。
          </div>
        </form>
      </section>
    </div>
  );
}